import { useGetChatUnreadCountQuery } from "@/setup/frontend/api/apiHooks";
import {
	useCaregiverAppGetShiftsQuery,
	useCaregiverAppGetPickupInvitationsQuery,
} from "@/setup/frontend/api/apiHooks";
import {
	Box,
	Group,
	Text,
	Stack,
	UnstyledButton,
	Indicator,
} from "@mantine/core";
import { GpsPermissionBanner } from "./GpsPermissionBanner";
import {
	IconCalendar,
	IconHandGrab,
	IconMessage,
	IconClockExclamation,
} from "@tabler/icons-react";
import { useNavigate } from "react-router";
import { getStableShiftsQueryAfterCursor } from "../utils";
import dayjs from "dayjs";

export function CaregiverAppLayout({
	children,
}: {
	children: React.ReactNode;
}) {
	const navigate = useNavigate();
	const unreadCount = useGetChatUnreadCountQuery();

	// Get missed shifts count
	const missedShiftsQuery = useCaregiverAppGetShiftsQuery({
		// use the same initial timestamp as the shifts query in the index page
		// this is to avoid the scenario where the caregiver sees an indicator >0 for missed shifts from previous pay period, navigates to the missed tab, and sees nothing.
		after: getStableShiftsQueryAfterCursor(dayjs()),
		status: "missing",
	});
	const missedCount = missedShiftsQuery.data?.pages?.flat().length || 0;

	// Get pickup invitations count
	const pickupInvitationsQuery = useCaregiverAppGetPickupInvitationsQuery();
	const pickupCount = pickupInvitationsQuery.data?.length || 0;

	return (
		<Box
			style={{
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				flex: 1,
			}}
		>
			{import.meta.env.VITE_CAPACITOR_BUILD === "true" ? (
				<GpsPermissionBanner />
			) : null}

			{/* Main Content Area */}
			<Box
				style={{
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					flex: 1,
				}}
			>
				{children}
			</Box>

			{/* Bottom Navigation */}
			<nav>
				<Group
					justify="space-around"
					align="center"
					p="xs"
					style={{
						borderTop:
							"1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))",
						backgroundColor:
							"light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))",
					}}
				>
					<UnstyledButton
						onClick={() => navigate("/app")}
						h="100%"
						c={
							location.pathname === "/app" &&
							!location.search.includes("status=missing")
								? "blue"
								: "dimmed"
						}
						p="xs"
					>
						<Stack gap="xs" align="center">
							<IconCalendar size={20} />
							<Text size="xs">Schedule</Text>
						</Stack>
					</UnstyledButton>
					<UnstyledButton
						onClick={() => navigate("/app?status=missing")}
						h="100%"
						c={
							location.pathname === "/app" &&
							location.search.includes("status=missing")
								? "blue"
								: "dimmed"
						}
						p="xs"
					>
						<Indicator
							color="orange"
							disabled={!missedCount}
							label={missedCount}
							size={16}
							position="top-end"
						>
							<Stack gap="xs" align="center">
								<IconClockExclamation size={20} />
								<Text size="xs">Missed</Text>
							</Stack>
						</Indicator>
					</UnstyledButton>
					<UnstyledButton
						onClick={() => navigate("/app/pickup")}
						h="100%"
						c={location.pathname === "/app/pickup" ? "blue" : "dimmed"}
						p="xs"
					>
						<Indicator
							color="blue"
							disabled={!pickupCount}
							label={pickupCount}
							size={16}
							position="top-end"
						>
							<Stack gap="xs" align="center">
								<IconHandGrab size={20} />
								<Text size="xs">Pickup</Text>
							</Stack>
						</Indicator>
					</UnstyledButton>

					<UnstyledButton
						onClick={() => navigate("/chat")}
						h="100%"
						c={location.pathname === "/chat" ? "blue" : "dimmed"}
						p="xs"
					>
						<Indicator
							color="red"
							disabled={!unreadCount.data?.unreadCount}
							label={unreadCount.data?.unreadCount}
							size={16}
							position="top-end"
						>
							<Stack gap="xs" align="center">
								<IconMessage size={20} />
								<Text size="xs">Chat</Text>
							</Stack>
						</Indicator>
					</UnstyledButton>
				</Group>
			</nav>
		</Box>
	);
}
